// Home indicator for an active Baby Monitor session. Polls
// /api/monitor/state every 30s — cheap and avoids holding a websocket
// open from every Home view in the family. Only renders if the Beta
// feature is enabled AND a Hub exists in the family.

import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useMe } from '../../api/hooks.ts';
import { fetchSnapshot, type MonitorSnapshot } from '../../lib/monitor.ts';

export function MonitorPill() {
  const enabled =
    typeof localStorage !== 'undefined' && localStorage.getItem('lt-beta-monitor') === '1';
  const { data: me } = useMe();
  const [snap, setSnap] = useState<MonitorSnapshot | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const tick = () => {
      fetchSnapshot()
        .then((s) => {
          if (alive) setSnap(s);
        })
        .catch(() => {
          /* ignore — no network / unauth */
        });
    };
    tick();
    const id = window.setInterval(tick, 30_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [enabled]);

  if (!enabled || !snap?.hub) return null;
  // Don't invite the hub to "join" their own session.
  const youAreHub = snap.hub.userId === me?.user.id;

  const count = snap.monitors.length;
  return (
    <Link
      to="/monitor"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        margin: '0 16px 10px',
        padding: '10px 14px',
        borderRadius: 999,
        background: 'color-mix(in srgb, var(--accent) 8%, var(--bg-card))',
        border: '1px solid color-mix(in srgb, var(--accent) 30%, var(--border))',
        color: 'var(--text-1)',
        textDecoration: 'none',
        fontSize: 13,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          background: 'var(--accent)',
          animation: 'lt-pulse 1.4s ease-in-out infinite',
        }}
      />
      <span style={{ flex: 1 }}>
        <strong style={{ fontWeight: 600 }}>
          {youAreHub ? 'You are broadcasting' : 'Monitor active'}
        </strong>{' '}
        · {snap.hub.deviceLabel}
        {count > 0 ? ` · ${count} listening` : ''}
      </span>
      <span style={{ color: 'var(--text-3)' }}>
        {youAreHub ? 'Tap to manage' : 'Tap to join'}
      </span>
    </Link>
  );
}
